import dayjs from 'dayjs';
import QuarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import { INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_METRICS_AND_SUBVALUES, INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_REFERENCE, INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_REFERENCE_LOOKUP, INSIGHTS_FACEBOOK_TIME_SERIES_ALL_METRICS, INSIGHTS_FACEBOOK_TIME_SERIES_COMPUTED_METRICS, INSIGHTS_FACEBOOK_TIME_SERIES_DATE_FIELDS, INSIGHTS_FACEBOOK_TIME_SERIES_MULTI_OPTION_FIELDS, INSIGHTS_FACEBOOK_TIME_SERIES_STANDARD_METRICS, INSIGHTS_FACEBOOK_TIME_SERIES_TEXT_FIELDS, INSIGHTS_TIME_SERIES_ALL_REFERENCE_BY_PLATFORM, } from './constants.js';
import { INSIGHTS_TIKTOK_ALL_FRONTEND_AVAILABLE_METRICS_AND_ATTRIBUTES } from './tiktok/metric.js';
dayjs.extend(QuarterOfYear);
const STANDARD_METRIC_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_STANDARD_METRICS);
export function isInsightsFacebookStandardMetric(metric) {
    return STANDARD_METRIC_SET.has(metric);
}
const TEXT_METRIC_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_TEXT_FIELDS);
export function isInsightsFacebookTextMetric(metric) {
    return TEXT_METRIC_SET.has(metric);
}
const MULTI_METRIC_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_MULTI_OPTION_FIELDS);
export function isInsightsFacebookEnumerableMetric(metric) {
    return MULTI_METRIC_SET.has(metric);
}
export function isInsightsFacebookTagMetric(metric) {
    if (metric === 'tag') {
        return true;
    }
    return false;
}
export function isInsightsFacebookTagCategoryMetric(metric) {
    if (metric === 'tagCategory') {
        return true;
    }
    return false;
}
const DATE_METRIC_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_DATE_FIELDS);
export function isInsightsFacebookDateMetric(metric) {
    return DATE_METRIC_SET.has(metric);
}
/** @deprecated */
export function isInsightsFacebookCustomMetric(metric) {
    return metric.startsWith('custom:');
}
const COMPUTED_METRICS_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_COMPUTED_METRICS);
export function isInsightsFacebookComputedMetric(metric) {
    return COMPUTED_METRICS_SET.has(metric);
}
export const ACTION_METRIC_REGEXP = /^action:(?<subvalue>cost_per|value|total|unique_cost_per|unique):(?<metric>.+)$/;
const SPECIFIC_ACTION_METRICS_SET = new Set(INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_METRICS_AND_SUBVALUES);
export function isInsightsFacebookSpecificActionMetric(metric) {
    if (SPECIFIC_ACTION_METRICS_SET.has(metric)) {
        return true;
    }
    return false;
}
export function isInsightsFacebookGenericActionMetric(metric) {
    return !!parseInsightsFacebookActionMetric(metric);
}
export function isInsightsFacebookMetricNumeric(metric) {
    return (isInsightsFacebookStandardMetric(metric) ||
        isInsightsFacebookSpecificActionMetric(metric) ||
        isInsightsFacebookGenericActionMetric(metric));
}
export function isInsightsFacebookFilterNumeric(filter) {
    return (isInsightsFacebookStandardMetric(filter.field) ||
        isInsightsFacebookSpecificActionMetric(filter.field) ||
        isInsightsFacebookGenericActionMetric(filter.field));
}
export function isInsightsFacebookFilterDate(filter) {
    if (isInsightsFacebookDateMetric(filter.field)) {
        return true;
    }
    return false;
}
export function isInsightsFacebookFilterText(filter) {
    if (isInsightsFacebookTextMetric(filter.field)) {
        return true;
    }
    return false;
}
export function isInsightsFacebookFilterEnumerable(filter) {
    if (isInsightsFacebookEnumerableMetric(filter.field)) {
        return true;
    }
    return false;
}
export function isInsightsFacebookFilterTag(filter) {
    if (isInsightsFacebookTagMetric(filter.field)) {
        return true;
    }
    return false;
}
export function isInsightsFacebookFilterTagCategory(filter) {
    if (isInsightsFacebookTagCategoryMetric(filter.field)) {
        return true;
    }
    return false;
}
export function stringifyInsightsFacebookActionMetric(actionMetric) {
    return `action:${actionMetric.subvalue}:${actionMetric.metric}`;
}
export function parseInsightsFacebookActionMetric(metric) {
    const matches = ACTION_METRIC_REGEXP.exec(metric);
    if (!matches?.groups || !matches.groups.subvalue || !matches.groups.metric) {
        return null;
    }
    if (matches.groups.subvalue !== 'cost_per' &&
        matches.groups.subvalue !== 'total' &&
        matches.groups.subvalue !== 'value' &&
        matches.groups.subvalue !== 'cost_per_unique' &&
        matches.groups.subvalue !== 'unique') {
        return null;
    }
    return {
        metric: matches.groups.metric,
        subvalue: matches.groups.subvalue,
    };
}
export function getMetricFormat(platform, metric) {
    const knownValue = INSIGHTS_TIME_SERIES_ALL_REFERENCE_BY_PLATFORM[platform][metric] ?? null;
    if (knownValue) {
        return knownValue.formatting;
    }
    const actionMetric = parseInsightsFacebookActionMetric(metric);
    if (actionMetric) {
        if (actionMetric.subvalue === 'cost_per' ||
            actionMetric.subvalue === 'value' ||
            actionMetric.subvalue === 'cost_per_unique') {
            return 'currency';
        }
    }
    return 'numeric';
}
export function getIsMetricEnumerable(platform, metric) {
    const knownValue = INSIGHTS_TIME_SERIES_ALL_REFERENCE_BY_PLATFORM[platform][metric] ?? null;
    return knownValue?.enumerable ?? false;
}
export function migrateFormerColumnMetricsToActionMetric(metric) {
    // Capture custom metrics and move them to action format
    if (metric.startsWith('custom:')) {
        if (metric.startsWith('custom:Cost Per ')) {
            return {
                metric: `offsite_conversion.fb_pixel_custom.${metric.replace('custom:Cost Per ', '')}`,
                subvalue: 'cost_per',
            };
        }
        else {
            return {
                metric: `offsite_conversion.fb_pixel_custom.${metric.replace('custom:', '')}`,
                subvalue: 'total',
            };
        }
    }
    // Existing action metrics moved to new format
    if (metric in INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_REFERENCE_LOOKUP) {
        return {
            metric: metric,
            subvalue: 'total',
        };
    }
    // Column level metrics that are now action metrics
    switch (metric) {
        case 'addToCarts':
            return {
                metric: 'offsite_conversion.fb_pixel_add_to_cart',
                subvalue: 'total',
            };
        case 'costPerAddToCarts':
            return {
                metric: 'offsite_conversion.fb_pixel_add_to_cart',
                subvalue: 'cost_per',
            };
        case 'appInstalls':
            return { metric: 'omni_app_install', subvalue: 'total' };
        case 'costPerAppInstall':
            return {
                metric: 'omni_app_install',
                subvalue: 'cost_per',
            };
        case 'linkClicks':
            return { metric: 'link_click', subvalue: 'total' };
        case 'costPerLinkClick':
            return { metric: 'link_click', subvalue: 'cost_per' };
        case 'leads':
            return { metric: 'lead', subvalue: 'total' };
        case 'costPerLead':
            return { metric: 'lead', subvalue: 'cost_per' };
        case 'purchase':
            return {
                metric: 'offsite_conversion.fb_pixel_purchase',
                subvalue: 'total',
            };
        case 'costPerPurchase':
            return {
                metric: 'offsite_conversion.fb_pixel_purchase',
                subvalue: 'cost_per',
            };
        default:
            return null;
    }
}
export function isInsightsFiltersV2(arr) {
    if (arr.length === 0)
        return true;
    const first = arr[0];
    if (first && 'field' in first) {
        return true;
    }
    return false;
}
export function convertDatePresetToDateRange(datePreset, relativeTo) {
    const now = relativeTo ? dayjs(relativeTo) : dayjs();
    let until;
    let since;
    switch (datePreset) {
        case 'today':
            /** Current day */
            until = since = now;
            break;
        case 'yesterday':
            /** Prior day */
            until = since = now.subtract(1, 'day');
            break;
        case 'last_3d':
            /** 3 days prior to current day (exclusive) */
            since = now.subtract(3, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'last_7d':
            /** 7 days prior to current day (exclusive) */
            since = now.subtract(7, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'this_week_sun_today':
            /** Most recent Sunday to today (inclusive) */
            since = now.startOf('week');
            until = now;
            break;
        case 'last_week_sun_sat':
            /** Week prior from Sunday to Saturday (inclusive) */
            since = now.subtract(1, 'week').startOf('week');
            until = now.subtract(1, 'week').endOf('week');
            break;
        case 'last_14d':
            /** 14 days prior to current day (exclusive) */
            since = now.subtract(14, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'last_28d':
            /** 28 days prior to current day (exclusive) */
            since = now.subtract(28, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'last_30d':
            /** 30 days prior to current day (exclusive) */
            since = now.subtract(30, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'last_90d':
            /** 30 days prior to current day (exclusive) */
            since = now.subtract(90, 'day');
            until = now.subtract(1, 'day');
            break;
        case 'this_month':
            /** From first day of the current month to today (inclusive) */
            since = now.startOf('month');
            until = now;
            break;
        case 'last_month':
            /** From first day to last day of the prior month (inclusive) */
            since = now.subtract(1, 'month').startOf('month');
            until = now.subtract(1, 'month').endOf('month');
            break;
        case 'last_quarter':
            /** From first day to last day of the most recently completed calendar  */
            since = now.subtract(1, 'quarter').startOf('quarter');
            until = now.subtract(1, 'quarter').endOf('quarter');
            break;
        case 'this_year':
            /** From first day of the current year to today (inclusive) */
            since = now.startOf('year');
            until = now;
            break;
        case 'last_year':
            /** From first day to last day of prior year (inclusive) */
            since = now.subtract(1, 'year').startOf('year');
            until = now.subtract(1, 'year').endOf('year');
            break;
        case 'maximum':
            /** From current day minus 3 years and 1 month to current day (inclusive) */
            since = now.subtract(3, 'year').subtract(1, 'month');
            until = now;
            break;
        case 'last_365d':
            /** Currently this is calculated on the backend to be inclusive of the current day */
            since = now.subtract(364, 'day');
            until = now;
            break;
        default:
            datePreset;
            throw new Error(`'datePreset' does not exist: ${datePreset}`);
    }
    return {
        since,
        until,
    };
}
const EMPTY = [];
export function getAllMetricsAndAttributesForPlatform(platform) {
    switch (platform) {
        case 'facebook':
            return INSIGHTS_FACEBOOK_TIME_SERIES_ALL_METRICS;
        case 'tiktok':
            return [
                ...new Set(INSIGHTS_TIKTOK_ALL_FRONTEND_AVAILABLE_METRICS_AND_ATTRIBUTES),
            ];
        default:
            return EMPTY;
    }
}
export function computeActionMetricNameWithSubvalue(name, subvalue) {
    if (subvalue === 'cost_per') {
        return `Cost Per ${name}`;
    }
    if (subvalue === 'value') {
        return `${name} (Value)`;
    }
    return name;
}
export function computeNameForCustomEvent(event, customEvents) {
    return (customEvents
        .find((x) => x === event)
        ?.replace('app_custom_event.', '')
        .replace('offsite_conversion.fb_pixel_custom.', '') || null);
}
export function getMetricPositivity(platform, metric) {
    const knownValue = INSIGHTS_TIME_SERIES_ALL_REFERENCE_BY_PLATFORM[platform][metric]?.higherIs ?? null;
    if (knownValue)
        return knownValue;
    if (metric.startsWith('action:')) {
        const knownAction = INSIGHTS_FACEBOOK_TIME_SERIES_ACTION_REFERENCE[metric]?.higherIs ?? null;
        if (knownAction)
            return knownAction;
        const match = parseInsightsFacebookActionMetric(metric);
        if (!match) {
            return null;
        }
        if (match.subvalue === 'cost_per' || match.subvalue === 'cost_per_unique') {
            return 'negative';
        }
        return 'neutral';
    }
    return 'neutral';
}
//# sourceMappingURL=utils.js.map